import * as React from "react";
import { useState } from "react";
import { Button, Input, Card, CardHeader, Text, Spinner, Link, Field } from "@fluentui/react-components";
import { PersonRegular, LockClosedRegular } from "@fluentui/react-icons";
import { useAuth } from "@/context/auth";
import { useRoute } from "@/context/router";
import { useLoginStyles } from "@/pages/login/styles";
import { YellowPadLogo } from "@/components/yellowpad-logo";

export const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const styles = useLoginStyles();
  const { setCurrentRoute } = useRoute();
  const { isLoadingLogin, login } = useAuth();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setError("");
      await login(email, password);
      setCurrentRoute("risk-analysis");
    } catch (error) {
      setError(error?.message || "Login failed. Please check your credentials.");
    }
  };

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <CardHeader
          header={
            <div className={styles.header}>
              <YellowPadLogo width={32} height={32} />
              <Text weight="semibold" size={500}>
                Log In to YellowPad
              </Text>
            </div>
          }
        />
        <form className={styles.form} onSubmit={handleSubmit}>
          <Field required label="Username">
            <Input
              required
              id="email"
              value={email}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              disabled={isLoadingLogin}
              className={styles.input}
              contentBefore={<PersonRegular className={styles.icon} />}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field required label="Password">
            <Input
              required
              id="password"
              type="password"
              autoCapitalize="off"
              autoCorrect="off"
              value={password}
              disabled={isLoadingLogin}
              className={styles.input}
              contentBefore={<LockClosedRegular className={styles.icon} />}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <Button
            iconPosition="after"
            disabled={isLoadingLogin}
            type="submit"
            appearance="primary"
            className={styles.button}
          >
            Log In
            {isLoadingLogin && <Spinner size="extra-tiny" className={styles.loading} />}
          </Button>
          <Text size={100} weight="regular">
            Sign-up is required. Please contact <Link href="mailto:sales@yellowpad.ai">sales@yellowpad.ai</Link> to
            obtain an account.
          </Text>
          <Link onClick={() => setCurrentRoute("forgot-password")} style={{ width: "fit-content" }}>
            Forgot Password?
          </Link>
          {error && <div className={styles.errorText}>{error}</div>}
        </form>
      </Card>
      {/* Okta sign-in is hidden: no customer uses it (one failed attempt in 30 days of production logs).
          The router is in-memory, so with this button gone the login/okta page is unreachable; restoring
          the button (route "login/okta") is the whole re-enable. */}
    </div>
  );
};
